import React, { useEffect, useState } from 'react';
import { useNotificationStore } from '../../store/notificationStore';
import { NotificationHistoryItem, NotificationType, NotificationStatus } from '../../types';
import { Button } from '../ui/Button';
import { clsx } from 'clsx';
import { 
  Bell, 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertCircle,
  Filter,
  Calendar,
  ExternalLink
} from 'lucide-react';

const statusIcons: Record<NotificationStatus, React.ReactNode> = {
  [NotificationStatus.Scheduled]: <Clock className="w-4 h-4 text-blue-500" />,
  [NotificationStatus.Sent]: <CheckCircle className="w-4 h-4 text-green-500" />,
  [NotificationStatus.Failed]: <XCircle className="w-4 h-4 text-red-500" />,
  [NotificationStatus.Cancelled]: <XCircle className="w-4 h-4 text-gray-500" />
};

const statusLabels: Record<NotificationStatus, string> = {
  [NotificationStatus.Scheduled]: 'Scheduled',
  [NotificationStatus.Sent]: 'Sent',
  [NotificationStatus.Failed]: 'Failed',
  [NotificationStatus.Cancelled]: 'Cancelled'
};

const typeLabels: Record<NotificationType, string> = {
  [NotificationType.HabitReminder]: 'Habit Reminder',
  [NotificationType.StreakMilestone]: 'Streak Milestone',
  [NotificationType.MissedHabit]: 'Missed Habit',
  [NotificationType.WeeklyReport]: 'Weekly Report',
  [NotificationType.Achievement]: 'Achievement',
  [NotificationType.StreakEndanger]: 'Streak Warning',
  [NotificationType.Motivation]: 'Motivation'
};

interface NotificationHistoryItemProps {
  notification: NotificationHistoryItem;
}

const NotificationHistoryItemComponent: React.FC<NotificationHistoryItemProps> = ({ notification }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: NotificationStatus) => {
    switch (status) {
      case NotificationStatus.Sent:
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case NotificationStatus.Failed:
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
      case NotificationStatus.Scheduled:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      case NotificationStatus.Cancelled:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <Bell className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                {notification.title}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {typeLabels[notification.type]}
              </p>
            </div>
          </div>
          
          <p className="text-gray-700 dark:text-gray-300 mb-3">
            {notification.body}
          </p>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
              <div className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span>Created: {formatDate(notification.createdAt)}</span>
              </div>
              {notification.sentAt && (
                <div className="flex items-center space-x-1">
                  <CheckCircle className="w-4 h-4" />
                  <span>Sent: {formatDate(notification.sentAt)}</span>
                </div>
              )}
            </div>
            
            {notification.actionUrl && notification.actionText && (
              <Button
                variant="ghost"
                size="sm"
                rightIcon={<ExternalLink className="w-4 h-4" />}
                onClick={() => window.open(notification.actionUrl, '_blank')}
              >
                {notification.actionText}
              </Button>
            )}
          </div>
        </div>
        
        <div className="ml-4 flex flex-col items-end space-y-2">
          <div className={clsx(
            'inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium',
            getStatusColor(notification.status)
          )}>
            {statusIcons[notification.status]}
            <span>{statusLabels[notification.status]}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export const NotificationHistory: React.FC = () => {
  const { history, isLoading, error, loadHistory, clearError } = useNotificationStore();
  const [filteredHistory, setFilteredHistory] = useState<NotificationHistoryItem[]>([]);
  const [statusFilter, setStatusFilter] = useState<NotificationStatus | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<NotificationType | 'all'>('all');

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    if (!history) {
      setFilteredHistory([]);
      return;
    }

    let filtered = history;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => item.status === statusFilter);
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter(item => item.type === typeFilter);
    }

    // Sort by creation date, newest first
    filtered = filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    setFilteredHistory(filtered);
  }, [history, statusFilter, typeFilter]);

  const handleRefresh = () => {
    loadHistory();
  };

  if (isLoading && (!history || history.length === 0)) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 px-4 md:px-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Notification History
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            View your past notifications and their delivery status
          </p>
        </div>
        
        <Button
          variant="outline"
          onClick={handleRefresh}
          isLoading={isLoading}
        >
          Refresh
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
              <span className="text-red-800 dark:text-red-200">{error}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={clearError}>
              Dismiss
            </Button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filters:</span>
          </div>
          
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600 dark:text-gray-400">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value) as NotificationStatus)}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="all">All</option>
              {Object.values(NotificationStatus).filter(value => typeof value === 'number').map((status) => (
                <option key={status} value={status}>{statusLabels[status as NotificationStatus]}</option>
              ))}
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600 dark:text-gray-400">Type:</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value) as NotificationType)}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="all">All</option>
              {Object.values(NotificationType).filter(value => typeof value === 'number').map((type) => (
                <option key={type} value={type}>{typeLabels[type as NotificationType]}</option>
              ))}
            </select>
          </div>
          
          {(statusFilter !== 'all' || typeFilter !== 'all') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setStatusFilter('all');
                setTypeFilter('all');
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* Notification List */}
      <div className="space-y-4">
        {filteredHistory.map((notification) => (
          <NotificationHistoryItemComponent
            key={notification.id}
            notification={notification}
          />
        ))}
      </div>

      {filteredHistory.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {(!history || history.length === 0) ? 'No notifications yet' : 'No notifications match your filters'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {(!history || history.length === 0) 
              ? 'Your notification history will appear here once you start receiving notifications.'
              : 'Try adjusting your filters to see more notifications.'
            }
          </p>
        </div>
      )}

      {/* Summary Stats */}
      {history && history.length > 0 && (
        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.values(NotificationStatus).filter(value => typeof value === 'number').map((status) => {
              const statusValue = status as NotificationStatus;
              const count = history.filter(item => item.status === statusValue).length;
              return (
                <div key={statusValue} className="text-center">
                  <div className="flex items-center justify-center mb-1">
                    {statusIcons[statusValue]}
                  </div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{count}</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">{statusLabels[statusValue]}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}; 